import styled from '@emotion/styled';
import { InputAdornment, TextField } from '@mui/material';
import Icons from '@shared/components/core/images/Icons';
import { color } from '@shared/constants/Styles';
import { ChangeEvent, FocusEvent, InputHTMLAttributes, KeyboardEvent, MouseEvent, Ref } from 'react';
import { useTranslation } from 'react-i18next';
import { IconName } from '../images/IconMap';

interface SearchFieldProps {
    label?: string;
    value: string;
    icon?: IconName;
    placeholder?: string;
    onChange: (event: ChangeEvent<HTMLInputElement>) => void;
    onClear: () => void;
    /** Set false when the parent provides a more persistent, semantic clear action. */
    showClearButton?: boolean;
    /** Id set on the underlying input, for an external <label htmlFor> or aria-controls wiring. */
    id?: string;
    /** Ref to the underlying input, for callers that need to focus it programmatically. */
    inputRef?: Ref<HTMLInputElement>;
    onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void;
    onFocus?: (event: FocusEvent<HTMLInputElement>) => void;
    onBlur?: (event: FocusEvent<HTMLInputElement>) => void;
    onClick?: (event: MouseEvent<HTMLInputElement>) => void;
    /**
     * Extra attributes for the underlying input — e.g. the `role`/`aria-*` set that turns this field into
     * a type-ahead combobox. Merged over the defaults, so an explicit `aria-label` here wins.
     */
    htmlInputProps?: InputHTMLAttributes<HTMLInputElement>;
}

function SearchField({
    label,
    placeholder = '',
    value = '',
    onChange,
    onClear,
    showClearButton = true,
    id,
    inputRef,
    icon = 'search',
    onKeyDown,
    onFocus,
    onBlur,
    onClick,
    htmlInputProps,
}: SearchFieldProps) {
    const { t } = useTranslation();
    // An explicit empty label hides the floating label (used where the placeholder is
    // enough, e.g. the AI History search); we still expose an aria-label for a11y.
    const hideLabel = label === '';
    const resolvedLabel = label || t('common:search');
    return (
        <>
            <StyledTextField
                onChange={onChange}
                onKeyDown={onKeyDown}
                onFocus={onFocus}
                onBlur={onBlur}
                onClick={onClick}
                id={id}
                inputRef={inputRef}
                size="small"
                fullWidth
                variant="outlined"
                placeholder={placeholder}
                label={hideLabel ? undefined : resolvedLabel}
                value={value}
                slotProps={{
                    input: {
                        startAdornment: (
                            <InputAdornment position="start">
                                <Icons name={icon} color={color.tertiary} />
                            </InputAdornment>
                        ),
                        endAdornment: (
                            <InputAdornment position="end">
                                {showClearButton && value && (
                                    <CloseButton onClick={onClear}>
                                        <Icons name="cancel2" ariaLabel={t('common:close')} />
                                    </CloseButton>
                                )}
                            </InputAdornment>
                        ),
                    },
                    htmlInput: {
                        ...(hideLabel ? { 'aria-label': placeholder || resolvedLabel } : {}),
                        ...htmlInputProps,
                    },
                }}
            />
        </>
    );
}

const StyledTextField = styled(TextField)`
    margin: 0.5rem 0;
    background: ${(props) => props.theme.color.background};
    border-radius: 40px;
    & .MuiOutlinedInput-root {
        border-radius: 40px;
    }
    input {
        &:focus {
            outline: none;
        }
    }
`;

const CloseButton = styled.button`
    background: none;
    border: none;
    color: ${(props) => props.theme.color.text};
`;

export default SearchField;
